// src/components/services/HealthInsurance.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineMedicalServices,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
    MdOutlineSecurity,
    MdOutlinePayment,
} from "react-icons/md";
import {
    FaPhoneAlt,
    FaHospital,
    FaAmbulance,
    FaShieldAlt,
    FaHandHoldingMedical,
} from "react-icons/fa";


function HealthInsurance() {
    const serviceName = "Health Insurance"; // Set service name

    // Animation Variants (Keep or move to utils/animations.js)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Health Insurance
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Protect yourself and your loved ones from unexpected medical expenses
                        with comprehensive Health Insurance plans from Keziah Global Private
                        Limited, in partnership with Fino Payments Bank. We offer a range of
                        coverage options to suit your needs and budget, ensuring you have
                        access to quality healthcare when you need it most.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaHospital className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Hospitalization Coverage
                                    </h4>
                                    <p className="text-gray-600">
                                        Get coverage for in-patient hospitalization expenses,
                                        including room rent, doctor's fees, and medical tests.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaAmbulance className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Pre- and Post-Hospitalization
                                    </h4>
                                    <p className="text-gray-600">
                                        Cover expenses incurred before and after hospitalization,
                                        such as consultations and medications.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineMedicalServices className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Day Care Procedures
                                    </h4>
                                    <p className="text-gray-600">
                                        Coverage for medical procedures that don't require overnight
                                        hospitalization.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineDescription className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Easy Claim Process
                                    </h4>
                                    <p className="text-gray-600">
                                        Hassle-free and quick claim settlement process.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaHandHoldingMedical className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Cashless Treatment
                                    </h4>
                                    <p className="text-gray-600">
                                        Avail cashless treatment at a wide network of hospitals.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaShieldAlt className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Comprehensive Coverage
                                    </h4>
                                    <p className="text-gray-600">
                                        Get comprehensive coverage from a trusted insurance provider.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Why Choose Keziah for Health Insurance */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Why Choose Keziah for Health Insurance
                        </h3>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                            <li>
                                Partnership with Fino Payments Bank, ensuring a wide network and
                                reliable service.
                            </li>
                            <li>
                                Wide range of health insurance plans to suit different needs and
                                budgets.
                            </li>
                            <li>
                                Personalized guidance from our team in Salem to help you choose
                                the right plan.
                            </li>
                            <li>
                                Simple and transparent application process.
                            </li>
                            <li>
                                Dedicated customer support to assist you with any queries or
                                claims.
                            </li>
                        </ul>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Get Your Health Insurance Quote Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, to discuss your health
                            insurance needs with our experts. You can also request a callback
                            to get personalized assistance and find the perfect plan for you
                            and your family.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                            <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default HealthInsurance;